import { useEffect, useState } from 'react'

import { useKnowledgeDocuments } from '@/models/knowledgeDocument'
import { DocumentEditor } from './KnowledgeTab/DocumentEditor'
import { KnowledgeTree } from './KnowledgeTab/KnowledgeTree'
import { VersionHistoryPanel } from './KnowledgeTab/VersionHistoryPanel'

type View = 'editor' | 'history'

function parseHash(): { doc: string | null; view: View } {
  const raw = window.location.hash.replace(/^#/, '')
  const [tab, query] = raw.split('?', 2)
  if (tab !== 'knowledge') return { doc: null, view: 'editor' }
  const params = new URLSearchParams(query || '')
  const view: View = params.get('view') === 'history' ? 'history' : 'editor'
  return { doc: params.get('doc'), view }
}

function writeHash(doc: string | null, view: View) {
  const params = new URLSearchParams()
  if (doc) params.set('doc', doc)
  if (view === 'history') params.set('view', 'history')
  const qs = params.toString()
  window.location.hash = qs ? `knowledge?${qs}` : 'knowledge'
}

export function KnowledgeTab() {
  const [state, setState] = useState(parseHash)
  const { data: docs } = useKnowledgeDocuments()

  useEffect(() => {
    function onHashChange() {
      setState(parseHash())
    }
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])

  function selectDoc(slug: string) {
    writeHash(slug, 'editor')
    setState({ doc: slug, view: 'editor' })
  }

  function openHistory() {
    if (!state.doc) return
    writeHash(state.doc, 'history')
    setState({ doc: state.doc, view: 'history' })
  }

  function closeHistory() {
    if (!state.doc) return
    writeHash(state.doc, 'editor')
    setState({ doc: state.doc, view: 'editor' })
  }

  useEffect(() => {
    if (!state.doc && docs?.length) {
      selectDoc(docs[0].slug)
    }
  }, [docs, state.doc])

  return (
    <div className="grid grid-cols-[320px_1fr] gap-6">
      <KnowledgeTree selectedSlug={state.doc} onSelect={selectDoc} />
      <div>
        {state.doc ? (
          state.view === 'history' ? (
            <VersionHistoryPanel slug={state.doc} onBack={closeHistory} />
          ) : (
            <DocumentEditor slug={state.doc} onOpenHistory={openHistory} />
          )
        ) : (
          <p className="text-gray-500">Select a document from the list or create a new one.</p>
        )}
      </div>
    </div>
  )
}
